/**
 * Pill — the column folded, fixed bottom-centre through any search (BRIEF §11, § Docked; one slot
 * since plan `2026_09_26_wall_hold_still_pill.md`). A mini-player: it shows the newest take, not
 * the pair it came from — two thumbnails side by side read as one false photograph where root and
 * take differ only by the face (Narcis, 2026-09-26). The slot is drawn at its picture's own ratio;
 * while the chain works it carries the cell at work (`WorkingCell`, the tile's own treatment).
 *
 * One primary target per state: `N ready` rings the slot and shows the filled Continue ↑; every
 * other state makes the whole pill the target, with a quiet ↑ where the button would be. A line only
 * while something is happening; a hairline along the bottom edge while rendering.
 */
import {Notice} from '@core/ui/Notice'
import {noticeKindFor} from './chains'
import {WorkingCell, type PreviewFrame} from './WorkingCell'
import styles from './Pill.module.scss'

const SLOT_HEIGHT = 44
const SLOT_MIN_WIDTH = 30
const SLOT_MAX_WIDTH = 58

/** The slot's width at its picture's own ratio, 44 px tall, clamped so a panorama or a sliver
 *  still reads as a thumbnail. */
export function pillSlotWidth(width: number, height: number): number {
    if (!(width > 0) || !(height > 0)) return SLOT_HEIGHT * (2 / 3)
    return Math.min(SLOT_MAX_WIDTH, Math.max(SLOT_MIN_WIDTH, Math.round((SLOT_HEIGHT * width) / height)))
}

export type PillProps = {
    rootLabel: string
    /** The one picture: the chain's newest done take, else its root's (the tile's original, the
     *  pack cover for a batch); `null` for a blank root with nothing done yet. */
    slotUrl: string | null
    /** The dimensions the slot is drawn at — the picture's own, or the pending row's for a blank. */
    slotWidth: number
    slotHeight: number
    /** Set while the chain has a row queued or rendering. */
    working: {status: 'queued' | 'rendering'; previewFrame: PreviewFrame | null} | null
    /** `N ready`: the ring and the filled Continue ↑. */
    ready: boolean
    line: string | null
    live: boolean
    onContinue: () => void
    onDismiss: () => void
}

export function Pill({
    rootLabel, slotUrl, slotWidth, slotHeight, working, ready, line, live, onContinue, onDismiss,
}: PillProps) {
    // A queued blank has nothing to breathe over yet: the dashed frame says "coming" on its own.
    const dashed = slotUrl === null && working?.status !== 'rendering'
    const slotClass = [styles.slot, dashed && styles.dashed, ready && styles.ring].filter(Boolean).join(' ')
    return (
        <div className={styles.wrap}>
            <div className={live ? `${styles.pill} ${styles.live}` : styles.pill}>
                {!ready && (
                    <button type="button" className={styles.hit} onClick={onContinue} aria-label={`Continue: ${rootLabel}`} />
                )}
                <span className={slotClass} style={{width: pillSlotWidth(slotWidth, slotHeight)}}>
                    {slotUrl ? <img src={slotUrl} alt="" /> : null}
                    {working && !dashed ? <WorkingCell previewFrame={working.previewFrame} /> : null}
                </span>
                <span className={styles.label}>
                    <span className={styles.name}>{rootLabel}</span>
                    {line && <Notice kind={noticeKindFor(line)} className={styles.state} ttlMs={0}>{line}</Notice>}
                </span>
                {ready ? (
                    <button type="button" className={styles.continueBtn} onClick={onContinue}>
                        Continue ↑
                    </button>
                ) : (
                    <span className={styles.chevron} aria-hidden="true">↑</span>
                )}
                <button type="button" className={styles.close} onClick={onDismiss} aria-label="Dismiss">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                        <path d="M5 5 L19 19" />
                        <path d="M19 5 L5 19" />
                    </svg>
                </button>
            </div>
        </div>
    )
}
